/**
 * safeStorage — a thin wrapper around localStorage that never throws.
 *
 * localStorage can throw in a surprising number of situations:
 * - Safari Private Browsing historically threw on every write
 * - Firefox's dom.storage.enabled=false makes even read throw
 * - Some corporate browsers quota out aggressively
 * - Third-party iframes with blocked storage
 * - Serialisation bugs (circular refs, BigInts)
 *
 * The previous codebase had ~5 inline `try { localStorage... } catch {}`
 * blocks. Every one of them silently swallowed errors, which means we
 * lost visibility into real bugs *and* couldn't tell when state had
 * legitimately gone missing.
 *
 * This wrapper:
 * - Never throws. All errors flow through `reportError` with a stable
 *   scope string so Sentry can group them.
 * - Returns `null` on read failure so callers can distinguish "absent"
 *   from "error" only by checking logs — callers don't need to branch
 *   on the failure mode.
 * - JSON helpers (getJSON / setJSON) handle serialisation errors.
 *
 * Usage:
 *   safeStorage.getJSON<{ foo: string }>('my_key')
 *   safeStorage.setJSON('my_key', { foo: 'bar' })
 *   safeStorage.remove('my_key')
 */

import { reportError } from './reportError';

function getItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    reportError('safeStorage.getItem', err, { key });
    return null;
  }
}

function setItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    reportError('safeStorage.setItem', err, { key });
    return false;
  }
}

function remove(key: string): boolean {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (err) {
    reportError('safeStorage.remove', err, { key });
    return false;
  }
}

function getJSON<T>(key: string): T | null {
  const raw = getItem(key);
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch (err) {
    reportError('safeStorage.parseJSON', err, { key });
    return null;
  }
}

function setJSON(key: string, value: unknown): boolean {
  try {
    const serialised = JSON.stringify(value);
    return setItem(key, serialised);
  } catch (err) {
    reportError('safeStorage.stringifyJSON', err, { key });
    return false;
  }
}

export const safeStorage = {
  getItem,
  setItem,
  remove,
  getJSON,
  setJSON,
};
